import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { useState } from 'react';
import { animated, useChain, useSpring, useSpringRef } from '@react-spring/web';
import { Waypoint } from 'react-waypoint';
import { MediaType } from '.';
import { IFileAttachment } from '../../../extViewmodels';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../models';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { IPixabayImage } from '../../../models/Pixabay';
import { THEME } from '../../components-v2/Theme/theme';
import { baseStyleSheet } from '../../styles/styles';
import { LoadingSpinner } from '../LoadingSpinner';
import { RefreshIcon } from '../svgs/icons/RefreshIcon';
import { SearchIcon } from '../svgs/icons/SearchIcon';
import { Input } from '../../components-v2/Input';
import { useMediaChooserContext } from './context';
import { useMediaChooserSearch } from './hooks/useMediaChooserSearch';
import { useMediaChooserSuggestions } from './hooks/useMediaChooserSuggestions';
import { MediaSuggestionResults } from './MediaSuggestionResults';
import { ImageContainerHeight, styleSheet } from './styles';

// Animated container component using modern hooks
const AnimatedImageContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	// First animation: height expansion
	const heightRef = useSpringRef();
	const heightAnimation = useSpring({
		ref: heightRef,
		from: { height: 0 },
		to: { height: ImageContainerHeight },
		config: { friction: 30, tension: 400 },
	});

	// Second animation: opacity fade in
	const opacityRef = useSpringRef();
	const opacityAnimation = useSpring({
		ref: opacityRef,
		from: { opacity: 0 },
		to: { opacity: 1 },
		config: { friction: 50, tension: 200 },
	});

	// Chain the animations: height first, then opacity
	useChain([heightRef, opacityRef], [0, 0.5]); // Start opacity animation at 50% of height animation

	return (
		<animated.div style={{ ...heightAnimation, ...opacityAnimation, overflow: 'hidden' }}>{children}</animated.div>
	);
};

interface IMediaChooserSearchProps extends IImpersonationContextComponentProps {
	mediaType?: MediaType;
	optionalFileName?: string;
	onSave?: (img: IFileAttachment) => void;
	onSaveWithAltText?: (img: IFileAttachment, altText?: string) => void;
	showAltTextInput?: boolean;
}

export const MediaChooserSearch: React.FC<IMediaChooserSearchProps> = inject(ImpersonationContextKey)(
	observer(props => {
		const { onSave, onSaveWithAltText, mediaType, optionalFileName, showAltTextInput = false } = props;
		const { selectedImage, setSelectedImage, createFileAttachmentFromSelectedImageAsync } = useMediaChooserContext();
		const { pushApiError } = useErrorMessages();
		const { logApiError } = useEventLogging('MediaChooserSearch');

		const {
			getMoreImages,
			inputRef,
			isLoading,
			onClearClicked,
			onCtaClicked,
			onInputKeyDown,
			onSearchTermInputChanged,
			pendingSearchTerm,
			searchedImages,
			searchTerm,
		} = useMediaChooserSearch();
		const [isUploading, setIsUploading] = useState(false);
		const [altText, setAltText] = useState('');

		const onImageClicked = (e: React.MouseEvent<HTMLElement>, selectedUserImage: IPixabayImage) => {
			e.preventDefault();
			setSelectedImage(selectedUserImage);
		};

		const onCancel = () => {
			setSelectedImage(null);
			setAltText('');
		};
		const handleChooseClick = async () => {
			setIsUploading(true);
			try {
				await onChooseClicked();
			} finally {
				setIsUploading(false);
			}
		};

		const onChooseClicked = async () => {
			try {
				const attachment = await createFileAttachmentFromSelectedImageAsync(optionalFileName);

				if (showAltTextInput && onSaveWithAltText) {
					onSaveWithAltText(attachment, altText);
				} else if (onSave) {
					onSave(attachment);
				}
			} catch (error) {
				logApiError('MediaChooser-CreateFileAttachment-Error', error);
				pushApiError(error);
			}
		};

		const onAltTextChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
			setAltText(e.target.value);
		};

		const renderSearch = () => {
			return (
				<div className={css(baseStyleSheet.verticalStack, styleSheet.search)}>
					<div>Search for royalty free images</div>
					<div className={css(baseStyleSheet.horizontalStack)}>
						<div className={css(styleSheet.searchInput)}>
							<Input
								autoComplete='off'
								id='freeimage-search-input'
								ref={inputRef}
								onChange={onSearchTermInputChanged}
								onKeyDown={onInputKeyDown}
								placeholder='Search for an image'
								type='text'
								value={pendingSearchTerm || ''}
							/>
						</div>
						<button
							className={searchedImages?.pages?.length > 0 ? undefined : css(styleSheet.hide)}
							onClick={onClearClicked}
						>
							<span className={css(styleSheet.clearSearch)}>Clear Search</span>
						</button>
					</div>
				</div>
			);
		};

		const renderResults = () => {
			if (selectedImage) {
				return null;
			}

			return mediaType === MediaType.Image && searchedImages?.pages[0].totalHits > 0 ? (
				<AnimatedImageContainer>
					<div className={css(styleSheet.imageGrid)}>
						{searchedImages?.pages?.map(page => {
							return page?.hits?.map(y => {
								return <PixabayImageTile key={y.id} image={y} onClick={onImageClicked} />;
							});
						})}
						<Waypoint bottomOffset='-200px' onEnter={() => getMoreImages()} />{' '}
					</div>
				</AnimatedImageContainer>
			) : (
				<figure className={css(styleSheet.noSuggestions, styleSheet.noSearchResults)}>
					<SearchIcon className={css(styleSheet.searchIcon)} fillColor={THEME.colors.secondary.main} />
					<span className={css(styleSheet.noSuggestionsTitle)}>No Images Found</span>
					<span className={css(styleSheet.noSuggestionsText)}>Please try a different search term.</span>
				</figure>
			);
		};
		return (
			<>
				{!selectedImage ? <div>{renderSearch()}</div> : null}
				{selectedImage ? (
					<div className={css(baseStyleSheet.verticalStack)}>
						<PixabayImageTile image={selectedImage} styles={[styleSheet.imageGridTileSelected]} />
						{showAltTextInput && (
							<div className={css(baseStyleSheet.verticalStack, styleSheet.altTextContainer)}>
								<div className={css(styleSheet.altTextLabel)}>Alt Text</div>
								<Input
									id='pixabay-alt-text-input'
									onChange={onAltTextChanged}
									placeholder='Alt text (optional but recommended for accessibility)'
									type='text'
									autoComplete='off'
									value={altText}
									autoFocus={false}
								/>
							</div>
						)}
					</div>
				) : null}
				{searchTerm && !isLoading ? renderResults() : null}
				<footer className={css(baseStyleSheet.horizontalStack, styleSheet.footer)}>
					{!searchedImages?.pages?.length && !selectedImage ? (
						<button className={css(baseStyleSheet.ctaButton)} onClick={onCtaClicked}>
							<span> Search</span>
						</button>
					) : selectedImage ? (
						<>
							<button className={css(baseStyleSheet.ctaButton)} onClick={handleChooseClick} disabled={isUploading}>
								<span>Add Image</span>
							</button>
							<button className={css(baseStyleSheet.ctaButton)} onClick={onCancel}>
								<span>Cancel</span>
							</button>
						</>
					) : null}
				</footer>
			</>
		);
	})
);

interface IMediaChooserSuggestionsProps extends IImpersonationContextComponentProps {
	content?: string;
	mediaType?: MediaType;
}

export const MediaChooserSuggestions: React.FC<IMediaChooserSuggestionsProps> = inject(ImpersonationContextKey)(
	observer(props => {
		const { mediaType, content } = props;
		const { showSuggestionsNoResults, showSuggestions, selectedImage, setSelectedImage } = useMediaChooserContext();

		const { onReloadClicked, keywords } = useMediaChooserSuggestions({
			content,
		});

		const onImageClicked = (selectedUserImage: IPixabayImage) => {
			setSelectedImage(selectedUserImage);
		};

		const noSelection = !selectedImage;

		const renderSuggestions = () => {
			const keywordsLabels: string[] = keywords;
			return (
				<section className={css(baseStyleSheet.verticalStack)}>
					<h4 className={css(styleSheet.suggestionSubHeader)}>
						Suggested images are based on keywords from your post content
					</h4>
					<div className={css(baseStyleSheet.horizontalStack, styleSheet.keywordsSection)}>
						<div>{'Keywords: '} </div>
						{keywordsLabels?.map(keyword => {
							return (
								<div className={css(styleSheet.keywordLabel)} key={keyword}>
									{keyword}
								</div>
							);
						})}
						{keywords?.length > 0 ? (
							<button onClick={onReloadClicked}>
								<RefreshIcon height={20} width={20} fillColor={THEME.colors.secondary.main} />
							</button>
						) : null}
					</div>
					{keywords?.length > 0 ? (
						<MediaSuggestionResults keywords={keywords} onImageClicked={onImageClicked} mediaType={mediaType} />
					) : (
						<LoadingSpinner />
					)}
				</section>
			);
		};

		return (
			<section
				className={css(
					showSuggestions && noSelection ? styleSheet.suggestionContainer : styleSheet.suggestionsContainerHidden
				)}
			>
				<h2 className={css(styleSheet.suggestionHeader)}>AI Suggested Images</h2>

				{!showSuggestionsNoResults && content?.length > 0 ? renderSuggestions() : null}
				{showSuggestionsNoResults || !content ? (
					<figure className={css(styleSheet.noSuggestions)}>
						<SearchIcon className={css(styleSheet.searchIcon)} fillColor={THEME.colors.secondary.main} />
						<span className={css(styleSheet.noSuggestionsTitle)}>No Suggestions Available</span>
						<span className={css(styleSheet.noSuggestionsText)}>
							To see suggested images, please provide more content to your post.
						</span>
					</figure>
				) : null}
			</section>
		);
	})
);

export interface IPixabayImageTileProps {
	image: IPixabayImage;
	onClick?(e: React.MouseEvent<HTMLElement>, image: IPixabayImage): void;
	styles?: StyleDeclarationValue[];
}

export const PixabayImageTile: React.FC<IPixabayImageTileProps> = ({ image, onClick, styles = [] }) => {
	const click = React.useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			onClick?.(e, image);
		},
		[onClick, image]
	);
	return (
		<button className={css(styleSheet.imageGridTile, ...styles)} onClick={click}>
			<img src={image.webformatURL} />
		</button>
	);
};
